import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { JJamesTableColumn } from '../../../types/ui.types';
import { cn } from '../../../utils/class-utils';

@Component({
    selector: 'jjames-table',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table class="min-w-full divide-y divide-gray-300">
        <!-- Header -->
        <thead class="bg-gray-50">
          <tr>
            @for (column of columns; track trackByKey($index, column)) {
              <th
                scope="col"
                [class]="getHeaderClasses(column)"
                [style.width]="column.width"
              >
                @if (column.sortable) {
                  <button
                    type="button"
                    class="group inline-flex items-center hover:bg-gray-100 rounded px-3 py-2 transition-colors"
                    (click)="onSort(column.key)"
                  >
                    {{ column.label }}
                    <span class="ml-2 flex-none rounded text-gray-400 group-hover:visible">
                      <svg
                        class="h-5 w-5"
                        [class.text-gray-900]="sortColumn === column.key"
                        [class.rotate-180]="sortColumn === column.key && sortDirection === 'desc'"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </button>
                } @else {
                  <span class="px-3 py-2">{{ column.label }}</span>
                }
              </th>
            }
          </tr>
        </thead>
        
        <!-- Body -->
        <tbody class="divide-y divide-gray-200 bg-white">
          @for (item of data; track trackByIndex($index, item); let i = $index) {
            <tr
              [class]="getRowClasses(item, i)"
              (click)="onRowClick(item, i)"
            >
              @for (column of columns; track trackByKey($index, column)) {
                <td [class]="getCellClasses(column)">
                  @if (hasCustomCell(column.key)) {
                    <ng-content [select]="'[slot=cell-' + column.key + ']'"></ng-content>
                  } @else {
                    {{ getNestedValue(item, column.key) }}
                  }
                </td>
              }
            </tr>
          }
          
          <!-- Empty state -->
          @if (data.length === 0) {
            <tr>
              <td [attr.colspan]="columns.length" class="px-6 py-4 text-sm text-gray-500 text-center">
                <div class="py-8">
                  <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 class="mt-2 text-sm font-medium text-gray-900">{{ emptyMessage }}</h3>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
    styles: []
})
export class JJamesTableComponent {
    @Input() columns: JJamesTableColumn[] = [];
    @Input() data: any[] = [];
    @Input() sortColumn?: string;
    @Input() sortDirection: 'asc' | 'desc' = 'asc';
    @Input() hoverable = true;
    @Input() striped = false;
    @Input() clickableRows = false;
    @Input() emptyMessage = 'No data available';

    @Output() sorted = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
    @Output() rowClicked = new EventEmitter<{ item: any; index: number }>();

    getHeaderClasses(column: JJamesTableColumn): string {
        const baseClasses = 'text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
        const sortableClasses = column.sortable ? 'cursor-pointer select-none' : '';

        return cn(baseClasses, sortableClasses);
    }

    getRowClasses(item: any, index: number): string {
        const baseClasses = 'transition-colors';
        const hoverClasses = this.hoverable ? 'hover:bg-gray-50' : '';
        const stripedClasses = this.striped && index % 2 === 1 ? 'bg-gray-50' : '';
        const clickableClasses = this.clickableRows ? 'cursor-pointer' : '';

        return cn(baseClasses, hoverClasses, stripedClasses, clickableClasses);
    }

    getCellClasses(column: JJamesTableColumn): string {
        return 'px-6 py-4 whitespace-nowrap text-sm text-gray-900';
    }

    onSort(columnKey: string): void {
        let direction: 'asc' | 'desc' = 'asc';

        if (this.sortColumn === columnKey) {
            direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
        }

        this.sortColumn = columnKey;
        this.sortDirection = direction;

        this.sorted.emit({ column: columnKey, direction });
    }

    onRowClick(item: any, index: number): void {
        if (this.clickableRows) {
            this.rowClicked.emit({ item, index });
        }
    }

    getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((value, key) => value?.[key], obj) ?? '';
    }

    hasCustomCell(columnKey: string): boolean {
        // This would need to be implemented based on projected content detection
        // For now, always return false to use default cell content
        return false;
    }

    trackByKey(index: number, column: JJamesTableColumn): string {
        return column.key;
    }

    trackByIndex(index: number, item: any): number {
        return index;
    }
}